import React, { useState, useEffect, useCallback } from "react";

const LIGHT_THEME = 'lara-light-teal';
const DARK_THEME = 'lara-dark-teal';

interface DarkModeContextType {
  darkMode: boolean;
  setDarkMode: (darkMode: boolean) => void;
}

const darkModeContext = React.createContext<DarkModeContextType>({
  darkMode: false,
  setDarkMode: () => {}
});

export function useDarkModeContext() {
  return React.useContext(darkModeContext);
}

function loadTheme(dark: boolean) {
  const themeId = 'app-theme';
  const themeName = dark ? DARK_THEME : LIGHT_THEME;
  const themeUrl = `https://cdn.jsdelivr.net/npm/primereact@10/resources/themes/${themeName}/theme.css`;

  let themeLink = document.getElementById(themeId) as HTMLLinkElement;

  if (!themeLink) {
    themeLink = document.createElement('link');
    themeLink.id = themeId;
    themeLink.rel = 'stylesheet';
    document.head.appendChild(themeLink);
  }

  themeLink.href = themeUrl;

  // Update body class for any custom dark mode styles
  document.body.classList.toggle('dark-mode', dark);
  document.getElementById('root')?.classList.toggle('darkRoot', dark);
}

export function useDefaultDarkModeState(defaultDarkMode: boolean) {
  const [darkMode, setDarkModeState] = useState(defaultDarkMode);

  const setDarkMode = useCallback((dark: boolean) => {
    setDarkModeState(dark);
    loadTheme(dark);
    localStorage.setItem('darkMode', dark ? 'true' : 'false');
  }, []);

  // Load theme on initial render
  useEffect(() => {
    loadTheme(defaultDarkMode);
  }, [defaultDarkMode]);

  return { darkMode, setDarkMode };
}

export default function DarkModeContextProvider({ children, value }: { children: React.ReactNode; value?: DarkModeContextType }) {
  const defaultValue: DarkModeContextType = {
    darkMode: false,
    setDarkMode: () => {}
  };

  return (
    <darkModeContext.Provider value={value || defaultValue}>
      {children}
    </darkModeContext.Provider>
  );
}
